import React, { useEffect, useRef } from 'react';
import { ChevronDown, Check, AlertCircle, RefreshCw } from 'lucide-react';
import { Cashier, getFullName, getInitials, getAvatarColor } from '../../types/cashier.types';
import { Skeleton } from '../common/Skeleton';
import './UserSelector.css';

export const CashierAvatar: React.FC<{ cashier: Cashier; size?: 'sm' | 'lg' }> = ({
    cashier,
    size = 'lg'
}) => {
    if (cashier.avatarBase64) {
        return (
            <img
                src={cashier.avatarBase64}
                alt=""
                className={`cashier-avatar cashier-avatar-${size} cashier-avatar-img`}
            />
        );
    }
    return (
        <div
            className={`cashier-avatar cashier-avatar-${size}`}
            style={{ backgroundColor: getAvatarColor(cashier) }}
            aria-hidden="true"
        >
            {getInitials(cashier)}
        </div>
    );
};

interface UserSelectorProps {
    cashiers: Cashier[];
    isLoading: boolean;
    error: string | null;
    selectedCashier: Cashier | null;
    isOpen: boolean;
    setIsOpen: (v: boolean) => void;
    onSelect: (c: Cashier) => void;
    onRetry: () => void;
}

export const UserSelector: React.FC<UserSelectorProps> = ({
    cashiers,
    isLoading,
    error,
    selectedCashier,
    isOpen,
    setIsOpen,
    onSelect,
    onRetry
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    // Cierra el desplegable al pulsar fuera (teclado numerico incluido) o con Escape.
    useEffect(() => {
        if (!isOpen) return;

        const onPointerDown = (e: PointerEvent) => {
            if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsOpen(false);
        };

        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen, setIsOpen]);

    if (isLoading) {
        return (
            <div className="user-selector">
                <div className="user-selector-current user-selector-current--skeleton al-skeleton-row" role="status">
                    <Skeleton width="3rem" height="3rem" radius="9999px" />
                    <div className="user-selector-current-text">
                        <Skeleton width="7rem" height="0.7rem" />
                        <Skeleton width="10rem" height="1rem" />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="user-selector-state">
                <AlertCircle className="user-selector-error-icon" />
                <span className="user-selector-error-text">{error}</span>
                <button type="button" onClick={onRetry} className="user-selector-retry-btn">
                    <RefreshCw className="user-selector-retry-icon" /> Reintentar
                </button>
            </div>
        );
    }

    if (cashiers.length === 0) {
        return (
            <div className="user-selector-state">
                <AlertCircle className="user-selector-error-icon" />
                <span className="user-selector-error-text">
                    No hay cajeros activos para este terminal.
                </span>
            </div>
        );
    }

    return (
        <div className="user-selector" ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="user-selector-current"
                aria-expanded={isOpen}
            >
                {selectedCashier && <CashierAvatar cashier={selectedCashier} />}
                <div className="user-selector-current-text">
                    <span className="user-selector-label">Iniciando sesion como</span>
                    <span className="user-selector-name">
                        {selectedCashier ? getFullName(selectedCashier) : 'Selecciona usuario'}
                    </span>
                </div>
                <ChevronDown
                    className={`user-selector-chevron ${isOpen ? 'user-selector-chevron-open' : ''}`}
                />
            </button>

            {isOpen && (
                <ul className="user-selector-list">
                    {cashiers.map((c) => (
                        <li key={c.id}>
                            <button
                                type="button"
                                onClick={() => {
                                    onSelect(c);
                                    setIsOpen(false);
                                }}
                                className={`user-selector-option ${
                                    selectedCashier?.id === c.id ? 'user-selector-option-active' : ''
                                }`}
                            >
                                <CashierAvatar cashier={c} size="sm" />
                                <span className="user-selector-option-name">{getFullName(c)}</span>
                                {selectedCashier?.id === c.id && (
                                    <Check className="user-selector-option-check" />
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
